class CollectionGateway extends HTMLElement {
  constructor() {
    super();
    this.selectedIndex = 0;
    this.hoverIndex = -1;
    this.isTouchRail = false;
    this.isFinePointer = false;
    this.reducedMotion = false;
    this.hasAnimated = false;
    this.marqueeRunning = false;
    this.marqueePaused = false;
    this.marqueeOffset = 0;
    this.marqueeSpeed = 58;
    this.marqueeLoopWidth = 0;
    this.lastFrameTime = 0;
    this.rafId = null;
    this.isTransitioning = false;
    this.nudgeDone = false;

    this.onPanelPointerEnter = this.onPanelPointerEnter.bind(this);
    this.onPanelPointerLeave = this.onPanelPointerLeave.bind(this);
    this.onPanelClick = this.onPanelClick.bind(this);
    this.onPanelKeydown = this.onPanelKeydown.bind(this);
    this.onRailPointerEnter = this.onRailPointerEnter.bind(this);
    this.onRailPointerLeave = this.onRailPointerLeave.bind(this);
    this.onRailFocusIn = this.onRailFocusIn.bind(this);
    this.onRailFocusOut = this.onRailFocusOut.bind(this);
    this.onResize = this.onResize.bind(this);
    this.marqueeTick = this.marqueeTick.bind(this);
  }

  connectedCallback() {
    this.panelsEl = this.querySelector('.cg__panels');
    this.panels = Array.from(this.querySelectorAll('.cg__panel'));
    this.railStage = this.querySelector('[data-rail-stage]');
    this.railTrack = this.querySelector('[data-rail-track]');
    this.railViewport = this.querySelector('.cg__rail-viewport');
    this.selectedNameEl = this.querySelector('[data-selected-name]');
    this.viewAllEl = this.querySelector('[data-view-all]');
    this.viewAllTextEl = this.querySelector('[data-view-all-text]');
    this.sectionId = this.dataset.sectionId;
    this.viewAllPrefix = this.dataset.viewAllPrefix || 'VIEW ALL';

    if (!this.panels.length || !this.railTrack) return;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.canDrift = !this.reducedMotion && typeof window.AlsDriftRail === 'function';
    this.pointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.isFinePointer = this.pointerQuery.matches;
    this.updateRailMode();

    this.bindEvents();
    this.loadCollection(0, { immediate: true, skipTransition: true });
    this.initObserver();

    this.pointerQuery.addEventListener('change', this.onResize);
    window.addEventListener('resize', this.onResize);
  }

  disconnectedCallback() {
    this.stopMarquee();
    this.observer?.disconnect();
    this.pointerQuery?.removeEventListener('change', this.onResize);
    window.removeEventListener('resize', this.onResize);
    this.unbindPanelEvents();
    this.railViewport?.removeEventListener('pointerenter', this.onRailPointerEnter);
    this.railViewport?.removeEventListener('pointerleave', this.onRailPointerLeave);
    this.railViewport?.removeEventListener('focusin', this.onRailFocusIn);
    this.railViewport?.removeEventListener('focusout', this.onRailFocusOut);
  }

  bindEvents() {
    this.panels.forEach((panel, index) => {
      panel.addEventListener('pointerenter', this.onPanelPointerEnter);
      panel.addEventListener('pointerleave', this.onPanelPointerLeave);
      panel.addEventListener('click', this.onPanelClick);
      panel.addEventListener('keydown', this.onPanelKeydown);
      panel.dataset.index = String(index);
    });

    this.railViewport?.addEventListener('pointerenter', this.onRailPointerEnter);
    this.railViewport?.addEventListener('pointerleave', this.onRailPointerLeave);
    this.railViewport?.addEventListener('focusin', this.onRailFocusIn);
    this.railViewport?.addEventListener('focusout', this.onRailFocusOut);
  }

  unbindPanelEvents() {
    this.panels.forEach((panel) => {
      panel.removeEventListener('pointerenter', this.onPanelPointerEnter);
      panel.removeEventListener('pointerleave', this.onPanelPointerLeave);
      panel.removeEventListener('click', this.onPanelClick);
      panel.removeEventListener('keydown', this.onPanelKeydown);
    });
  }

  updateRailMode() {
    this.isFinePointer = this.pointerQuery.matches;
    const isDrift = !this.isFinePointer && this.canDrift;
    const mode = this.isFinePointer ? 'marquee' : isDrift ? 'drift' : 'touch';
    if (mode === this.railMode) return;
    this.railMode = mode;
    this.isDrift = isDrift;
    this.isTouchRail = mode === 'touch';
    this.classList.toggle('is-touch-rail', this.isTouchRail);
    this.classList.toggle('is-drift-rail', this.isDrift);

    if (this.isTouchRail) {
      this.stopMarquee();
    } else if (this.classList.contains('is-products-visible') && !this.reducedMotion) {
      this.setupMarquee();
    }
  }

  onResize() {
    this.updateRailMode();
    if (this.railMode === 'marquee' && this.marqueeRunning) {
      this.measureMarquee();
    }
  }

  initObserver() {
    if (this.reducedMotion) {
      this.classList.add('is-visible', 'is-meta-visible', 'is-products-visible');
      this.panels.forEach((panel) => panel.classList.add('is-revealed'));
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.hasAnimated) {
            this.hasAnimated = true;
            this.runEntrance();
            this.observer.disconnect();
          }
        });
      },
      { threshold: 0.18 }
    );

    this.observer.observe(this);
  }

  runEntrance() {
    this.classList.add('is-visible');

    window.setTimeout(() => {
      this.panels.forEach((panel, i) => {
        window.setTimeout(() => panel.classList.add('is-revealed'), i * 120);
      });
    }, 420);

    window.setTimeout(() => {
      this.classList.add('is-meta-visible');
    }, 1100);

    window.setTimeout(() => {
      this.classList.add('is-products-visible');
      this.staggerProductsIn();

      window.setTimeout(() => {
        if (!this.isTouchRail && !this.reducedMotion) {
          this.setupMarquee();
        } else if (this.isTouchRail && !this.nudgeDone && !this.reducedMotion) {
          this.runMobileNudge();
        }
      }, 280);
    }, 1280);
  }

  staggerProductsIn() {
    const products = this.railTrack?.querySelectorAll('.cg__product:not([aria-hidden="true"])') || [];
    products.forEach((product, i) => {
      product.style.transitionDelay = `${i * 60}ms`;
    });
  }

  runMobileNudge() {
    if (!this.railViewport || this.nudgeDone) return;
    this.nudgeDone = true;
    const start = this.railViewport.scrollLeft;
    this.railViewport.scrollTo({ left: start + 16, behavior: 'smooth' });
    window.setTimeout(() => {
      this.railViewport.scrollTo({ left: start, behavior: 'smooth' });
    }, 420);
  }

  onPanelPointerEnter(event) {
    if (!this.isFinePointer) return;
    const index = parseInt(event.currentTarget.dataset.index, 10);
    if (Number.isNaN(index)) return;
    this.hoverIndex = index;
    this.panelsEl.dataset.hover = String(index);
  }

  onPanelPointerLeave(event) {
    if (!this.isFinePointer) return;
    const related = event.relatedTarget;
    if (related && this.panelsEl.contains(related)) return;
    this.hoverIndex = -1;
    this.panelsEl.dataset.hover = '-1';
  }

  onPanelClick(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10);
    if (Number.isNaN(index)) return;

    if (!this.isFinePointer) {
      this.panels.forEach((p) => p.classList.remove('is-active-tap'));
      event.currentTarget.classList.add('is-active-tap');
      window.setTimeout(() => event.currentTarget.classList.remove('is-active-tap'), 180);
    }

    if (index === this.selectedIndex) return;
    this.selectCollection(index);
  }

  onPanelKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    const index = parseInt(event.currentTarget.dataset.index, 10);
    if (!Number.isNaN(index)) this.selectCollection(index);
  }

  selectCollection(index) {
    if (this.isTransitioning || index === this.selectedIndex) return;
    this.selectedIndex = index;
    this.panelsEl.dataset.selected = String(index);

    this.panels.forEach((panel, i) => {
      const isSelected = i === index;
      panel.classList.toggle('is-selected', isSelected);
      panel.classList.toggle('is-quiet', !isSelected);
      panel.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    this.updateMeta(index);
    this.loadCollection(index, { skipTransition: false });
  }

  updateMeta(index) {
    const panel = this.panels[index];
    if (!panel) return;
    const title = panel.dataset.collectionTitle || '';
    const url = panel.dataset.collectionUrl || '#';

    if (this.selectedNameEl) this.selectedNameEl.textContent = title;
    if (this.viewAllEl) this.viewAllEl.href = url;
    if (this.viewAllTextEl) {
      this.viewAllTextEl.textContent = `${this.viewAllPrefix} ${title.toUpperCase()}`;
    }
  }

  loadCollection(index, options = {}) {
    const { immediate = false, skipTransition = false } = options;
    const template = document.getElementById(`cg-products-${this.sectionId}-${index}`);
    if (!template) return;

    const applyContent = () => {
      this.railTrack.innerHTML = '';
      const fragment = template.content.cloneNode(true);
      this.railTrack.appendChild(fragment);

      const cloneTrack = this.railTrack.cloneNode(true);
      cloneTrack.setAttribute('aria-hidden', 'true');
      cloneTrack.classList.add('cg__rail-track--clone');
      cloneTrack.querySelectorAll('.cg__product').forEach((item) => {
        item.setAttribute('tabindex', '-1');
        item.setAttribute('aria-hidden', 'true');
      });

      this.railStage.querySelectorAll('.cg__rail-track--clone').forEach((el) => el.remove());
      this.railStage.appendChild(cloneTrack);

      if (this.isTouchRail) {
        this.railStage.querySelectorAll('.cg__rail-track--clone').forEach((el) => el.remove());
      }

      if (this.isTouchRail && this.railViewport) {
        this.railViewport.scrollLeft = 0;
      }

      this.railStage.classList.remove('is-transitioning-out', 'is-transitioning-in');
      this.classList.add('is-products-visible');
      this.staggerProductsIn();

      if (!this.isTouchRail && !this.reducedMotion && this.classList.contains('is-visible')) {
        window.setTimeout(() => this.setupMarquee(), immediate ? 0 : 320);
      }
    };

    if (skipTransition || immediate || this.reducedMotion) {
      this.stopMarquee();
      applyContent();
      return;
    }

    this.isTransitioning = true;
    this.stopMarquee();
    this.marqueeOffset = 0;
    if (this.railStage) this.railStage.style.transform = 'translate3d(0, 0, 0)';
    this.railStage.classList.add('is-transitioning-out');

    window.setTimeout(() => {
      applyContent();
      this.railStage.classList.add('is-transitioning-in');

      window.requestAnimationFrame(() => {
        this.railStage.classList.remove('is-transitioning-in');
        this.isTransitioning = false;
      });
    }, 220);
  }

  setupMarquee() {
    if (this.isTouchRail || this.reducedMotion) return;

    this.stopMarquee();

    if (this.isDrift) {
      if (!this.drift) {
        this.drift = new window.AlsDriftRail({
          viewport: this.railViewport,
          stage: this.railStage,
          speed: 48,
          getItems: () => this.railStage.querySelectorAll('.cg__product'),
          getLoopWidth: () => {
            const clone = this.railStage.querySelector('.cg__rail-track--clone');
            return clone ? clone.offsetLeft - this.railTrack.offsetLeft : 0;
          },
        });
      }
      this.marqueeRunning = true;
      this.drift.start({ reset: true });
      return;
    }

    this.measureMarquee();

    if (this.marqueeLoopWidth <= 0) return;

    const durationSeconds = this.marqueeLoopWidth / this.marqueeSpeed;
    this.marqueeDuration = Math.max(35, Math.min(55, durationSeconds));

    this.marqueeRunning = true;
    this.marqueePaused = false;
    this.lastFrameTime = performance.now();
    this.rafId = requestAnimationFrame(this.marqueeTick);
  }

  measureMarquee() {
    if (!this.railTrack || !this.railStage) return;

    const viewportWidth = this.railViewport?.offsetWidth || 0;
    let width = this.railTrack.scrollWidth;

    if (width < viewportWidth * 1.2) {
      const cloneTrack = this.railStage.querySelector('.cg__rail-track--clone');
      if (cloneTrack && cloneTrack.children.length === 0) {
        const extraClone = this.railTrack.cloneNode(true);
        extraClone.setAttribute('aria-hidden', 'true');
        extraClone.classList.add('cg__rail-track--clone');
        extraClone.querySelectorAll('.cg__product').forEach((item) => {
          item.setAttribute('tabindex', '-1');
          item.setAttribute('aria-hidden', 'true');
        });
        this.railStage.appendChild(extraClone);
      }
      width = this.railTrack.scrollWidth;
    }

    this.marqueeLoopWidth = width;
    this.marqueeSpeed = width > 2400 ? 62 : 55;
  }

  marqueeTick(now) {
    if (!this.marqueeRunning) return;

    const delta = now - this.lastFrameTime;
    this.lastFrameTime = now;

    if (!this.marqueePaused && this.marqueeLoopWidth > 0) {
      this.marqueeOffset -= (this.marqueeSpeed * delta) / 1000;

      if (this.marqueeOffset <= -this.marqueeLoopWidth) {
        this.marqueeOffset += this.marqueeLoopWidth;
      }

      this.railStage.style.transform = `translate3d(${this.marqueeOffset}px, 0, 0)`;
    }

    this.rafId = requestAnimationFrame(this.marqueeTick);
  }

  stopMarquee() {
    this.marqueeRunning = false;
    this.drift?.stop();
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  pauseMarquee() {
    this.marqueePaused = true;
  }

  resumeMarquee() {
    if (!this.isTouchRail && !this.reducedMotion && this.classList.contains('is-products-visible')) {
      this.marqueePaused = false;
    }
  }

  onRailPointerEnter() {
    this.pauseMarquee();
  }

  onRailPointerLeave() {
    this.resumeMarquee();
  }

  onRailFocusIn() {
    this.pauseMarquee();
  }

  onRailFocusOut(event) {
    if (this.railViewport && !this.railViewport.contains(event.relatedTarget)) {
      this.resumeMarquee();
    }
  }
}

if (!customElements.get('collection-gateway')) {
  customElements.define('collection-gateway', CollectionGateway);
}

// Theme editor section reload support
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('collection-gateway')) {
    event.target.querySelectorAll('collection-gateway').forEach((el) => {
      if (!el.connectedCallback) return;
    });
  }
});
